// Mirrored from cloud/functions/src/domain/schedules.ts.
// Single source of truth lives there; keep these in sync until both
// codebases consume a shared @ufh/domain workspace package.
//
// Frontend-only subset: wire shapes only. The cloud module also
// exports `denormalizeSchedules` and its twin-shape helpers
// (TwinSchedules, SetbackTwinEntry, SolarTwinEntry, etc.) — those
// are firmware-targeting concerns and are intentionally omitted here.

// Schedule shapes (Cosmos source-of-truth + twin denormalized form)
// plus the pure denormalizer that bridges between them.
//
// Sources of truth:
//   - data-model.md "Schedule" section: the Cosmos doc shape
//   - contracts/device-twin.md "Desired properties": the denormalized
//     twin shape the firmware actually consumes
//
// The two shapes differ deliberately. The Cosmos doc is a list of
// natural-language intervals ("mon 22:00-06:00") that's easy to edit
// from a dashboard; the twin shape is a compact, mode-targeted,
// bitmask form the firmware can evaluate per cycle without parsing
// strings (FR-007, the per-cycle pipeline performance budget).

import { z } from 'zod';
import { FloorName, GroupName } from './hardware-names';

export const ScheduleKind = z.enum(['setback', 'solar']);
export type ScheduleKind = z.infer<typeof ScheduleKind>;

// Schedule `target` vocabulary:
//   - setback schedules attach to a floor (one sensor per floor)
//   - solar schedules attach to a group (per-group mode-aware effect)
export const ScheduleTarget = z.union([FloorName, GroupName]);
export type ScheduleTarget = z.infer<typeof ScheduleTarget>;

export const Day = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
export type Day = z.infer<typeof Day>;

// HH:MM (24h). Server-side validation only — string is what the
// dashboard sends and what the Cosmos doc holds.
const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const ScheduleInterval = z.object({
  day: Day,
  start: TimeOfDay,
  end:   TimeOfDay,
});
export type ScheduleInterval = z.infer<typeof ScheduleInterval>;

export const ScheduleDoc = z.object({
  id: z.string(),                            // `schedule:<kind>:<target>:<uuid>`
  deviceId: z.string(),
  kind: ScheduleKind,
  target: ScheduleTarget,
  intervals: z.array(ScheduleInterval),
  setback_c: z.number().optional(),          // setback kind only
  active: z.boolean(),
  created_at: z.string(),                    // ISO-8601
  updated_at: z.string(),                    // ISO-8601
}).refine(
  // setback kind MUST carry a setback_c; solar kind MUST NOT.
  (s) => (s.kind === 'setback' ? s.setback_c !== undefined : s.setback_c === undefined),
  { message: 'setback schedules require setback_c; solar schedules must not carry one' },
);
export type ScheduleDoc = z.infer<typeof ScheduleDoc>;
